import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Badge } from '@/components/ui/badge';
import { X, CheckCircle2 } from 'lucide-react';

const comparisons = [
  { aspect: 'WhatsApp Business API', traditional: 'Required (complex approval)', agentick: 'Not required' },
  { aspect: 'Setup Time', traditional: '2-4 weeks', agentick: 'Under 5 minutes' },
  { aspect: 'Technical Knowledge', traditional: 'Developer needed', agentick: 'No coding required' },
  { aspect: 'Monthly Cost', traditional: '₹15,000 - ₹50,000+', agentick: 'Starts affordable' },
  { aspect: 'AI Responses', traditional: 'Basic templates only', agentick: 'Context-aware AI' },
  { aspect: 'Knowledge Updates', traditional: 'Manual coding changes', agentick: 'Upload & update instantly' },
  { aspect: 'Free Trial', traditional: 'Rarely offered', agentick: '7 days, full access' },
];

export const WhyAgentick = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="why-agentick" ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-primary/[0.03] blur-[100px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="accent" className="mb-4">
              The Agentick Advantage
            </Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight"
          >
            Traditional Setup vs{' '}
            <span className="gradient-text-primary">Agentick</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed"
          >
            See why hundreds of businesses are making the switch.
          </motion.p>
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="glass-card rounded-2xl overflow-hidden"
        >
          {/* Table Header */}
          <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-secondary/50 border-b border-border">
            <div className="text-sm font-medium text-muted-foreground font-body">Feature</div>
            <div className="text-sm font-medium text-muted-foreground font-body text-center">Traditional</div>
            <div className="text-sm font-medium text-primary font-heading text-center">Agentick</div>
          </div>

          {/* Table Body */}
          {comparisons.map((row, i) => (
            <motion.div
              key={row.aspect}
              initial={{ opacity: 0, x: -10 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.4 + i * 0.07 }}
              className={`grid grid-cols-3 gap-4 px-6 py-4 ${
                i < comparisons.length - 1 ? 'border-b border-border/50' : ''
              } hover:bg-secondary/30 transition-colors duration-200`}
            >
              <div className="text-sm text-foreground font-medium font-body">{row.aspect}</div>
              <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground font-body">
                <X size={14} className="text-destructive/60 shrink-0" />
                <span className="text-center hidden sm:inline">{row.traditional}</span>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-sm text-foreground font-body">
                <CheckCircle2 size={14} className="text-primary shrink-0" />
                <span className="text-center hidden sm:inline">{row.agentick}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
